import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, faqApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import Pagination from '../components/Pagination';
import '../styles/admin-dashboard.css';

const PRESET_CATEGORIES = [
  'For Professionals',
  'For Users',
  'General',
  'Booking & Sessions',
  'Pricing & Payments',
  'Privacy & Security',
  'Account & Profile',
  'Technical Support',
];

const AUDIENCE_LABELS = {
  all: { label: 'All Users', color: '#6366f1', bg: '#e0e7ff', icon: '🌐' },
  clients: { label: 'Clients', color: '#0ea5e9', bg: '#e0f2fe', icon: '👤' },
  therapists: { label: 'Therapists', color: '#8b5cf6', bg: '#ede9fe', icon: '👨‍⚕️' },
  general: { label: 'General Public', color: '#10b981', bg: '#d1fae5', icon: '📢' },
};

function AdminFaqsPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0, categoriesCount: 0 });

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('order');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Accordion expanded for preview
  const [expandedFaqId, setExpandedFaqId] = useState(null);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  // Form Data
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'For Professionals',
    targetAudience: 'all',
    order: 1,
    isPublished: true,
  });

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null, loading: false });

  // Toast Feedback State
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4500);
  };

  const fetchFaqs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await faqApi.adminGetFaqs({
        search: searchQuery,
        category: categoryFilter,
        audience: audienceFilter,
        status: statusFilter,
        sortBy,
      });

      if (res?.success) {
        setFaqs(res.faqs || []);
        if (res.categories) {
          setCategories(res.categories);
        }
        if (res.stats) {
          setStats(res.stats);
        }
      }
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      showFeedback('error', err.message || 'Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, audienceFilter, statusFilter, sortBy]);

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchFaqs();
  }, [fetchFaqs, authSession?.token, authSession?.user?.role, navigate]);

  // Open modal for Create
  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setFormData({
      question: '',
      answer: '',
      category: 'For Professionals',
      targetAudience: 'all',
      order: faqs.length + 1,
      isPublished: true,
    });
    setModalError('');
    setIsModalOpen(true);
  };

  // Open modal for Edit
  const handleOpenEditModal = (faq) => {
    setModalMode('edit');
    setEditingId(faq._id);
    setFormData({
      question: faq.question || '',
      answer: faq.answer || '',
      category: faq.category || 'General',
      targetAudience: faq.targetAudience || 'all',
      order: faq.order !== undefined ? faq.order : 1,
      isPublished: faq.isPublished !== undefined ? faq.isPublished : true,
    });
    setModalError('');
    setIsModalOpen(true);
  };

  // Handle Form Submit (Create / Update)
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setModalError('');

    if (!formData.question.trim()) {
      setModalError('Question is required.');
      return;
    }
    if (!formData.answer.trim()) {
      setModalError('Answer is required.');
      return;
    }

    try {
      setFormSubmitting(true);
      if (modalMode === 'create') {
        const res = await faqApi.adminCreateFaq(formData);
        if (res?.success) {
          showFeedback('success', 'FAQ question created successfully!');
          setIsModalOpen(false);
          fetchFaqs();
        } else {
          setModalError(res?.message || 'Failed to create FAQ');
        }
      } else {
        const res = await faqApi.adminUpdateFaq(editingId, formData);
        if (res?.success) {
          showFeedback('success', 'FAQ question updated successfully!');
          setIsModalOpen(false);
          fetchFaqs();
        } else {
          setModalError(res?.message || 'Failed to update FAQ');
        }
      }
    } catch (err) {
      console.error('Error saving FAQ:', err);
      setModalError(err.message || 'An error occurred while saving.');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Quick Toggle Publish Status
  const handleTogglePublish = async (faq) => {
    try {
      const res = await faqApi.adminTogglePublish(faq._id);
      if (res?.success) {
        showFeedback(
          'success',
          `FAQ "${faq.question.slice(0, 30)}..." is now ${res.isPublished ? 'Published' : 'Draft'}`
        );
        setFaqs((prev) =>
          prev.map((item) => (item._id === faq._id ? { ...item, isPublished: res.isPublished } : item))
        );
        setStats((prev) => ({
          ...prev,
          published: res.isPublished ? prev.published + 1 : prev.published - 1,
          drafts: res.isPublished ? prev.drafts - 1 : prev.drafts + 1,
        }));
      }
    } catch (err) {
      showFeedback('error', err.message || 'Failed to toggle status');
    }
  };

  // Move Order Up / Down
  const handleMoveOrder = async (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= faqs.length) return;

    const currentFaq = faqs[index];
    const targetFaq = faqs[targetIndex];

    const currentOrder = currentFaq.order || index + 1;
    const targetOrder = targetFaq.order || targetIndex + 1;

    try {
      const newOrders = [
        { id: currentFaq._id, order: targetOrder },
        { id: targetFaq._id, order: currentOrder },
      ];

      await faqApi.adminReorderFaqs(newOrders);
      showFeedback('success', 'Order updated');
      fetchFaqs();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to reorder');
    }
  };

  // Delete Action
  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      const res = await faqApi.adminDeleteFaq(deleteModal.item._id);
      if (res?.success) {
        showFeedback('success', 'FAQ deleted successfully!');
        setDeleteModal({ isOpen: false, item: null, loading: false });
        fetchFaqs();
      }
    } catch (err) {
      showFeedback('error', err.message || 'Failed to delete FAQ');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
    setAudienceFilter('all');
    setStatusFilter('all');
    setSortBy('order');
    setCurrentPage(1);
  };

  // Pagination calculation
  const totalPages = Math.ceil(faqs.length / itemsPerPage) || 1;
  const paginatedFaqs = faqs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="admin-layout" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex' }}>
      <DashboardSidebar userRole="admin" />

      <main
        className="sidebar-content-wrapper"
        style={{
          flex: 1,
          padding: '36px 40px',
          overflowY: 'auto',
          background: '#f8fafc',
          color: '#1e293b',
        }}
      >
        {/* Top Header Banner */}
        <div
          style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)',
            borderRadius: '20px',
            padding: '36px 40px',
            color: 'white',
            marginBottom: '32px',
            boxShadow: '0 12px 32px rgba(99, 102, 241, 0.18)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <span style={{ fontSize: '32px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>❓</span>
              <h1 style={{ margin: 0, fontSize: '30px', fontWeight: '800', letterSpacing: '-0.5px' }}>
                FAQ Management
              </h1>
            </div>
            <p style={{ margin: 0, fontSize: '15px', opacity: 0.9, maxWidth: '650px', lineHeight: '1.5' }}>
              Create, edit, organize, and publish frequently asked questions for clients and therapists on the platform.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleOpenCreateModal}
              style={{
                background: '#ffffff',
                color: '#4f46e5',
                border: 'none',
                padding: '14px 24px',
                borderRadius: '12px',
                fontWeight: '700',
                fontSize: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
              }}
            >
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span> Add New FAQ
            </button>
          </div>
        </div>

        {/* Feedback Alert Toast */}
        {feedback.message && (
          <div
            style={{
              marginBottom: '24px',
              padding: '16px 20px',
              borderRadius: '12px',
              fontWeight: 600,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
              background: feedback.type === 'error' ? '#fef2f2' : '#f0fdf4',
              color: feedback.type === 'error' ? '#991b1b' : '#166534',
              border: `1px solid ${feedback.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
            }}
          >
            <span style={{ fontSize: '18px' }}>{feedback.type === 'error' ? '⚠️' : '✅'}</span>
            <span>{feedback.message}</span>
          </div>
        )}

        {/* Stats Row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '20px',
            marginBottom: '32px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '22px 24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '12px',
                background: '#eff6ff',
                color: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
              }}
            >
              📋
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                Total Questions
              </div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', marginTop: '2px' }}>
                {stats.total || faqs.length}
              </div>
            </div>
          </div>

          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '22px 24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '12px',
                background: '#f0fdf4',
                color: '#16a34a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
              }}
            >
              ✅
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                Published Live
              </div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#16a34a', marginTop: '2px' }}>
                {stats.published}
              </div>
            </div>
          </div>

          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '22px 24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '12px',
                background: '#fffbeb',
                color: '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
              }}
            >
              🔒
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                Drafts / Hidden
              </div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#d97706', marginTop: '2px' }}>
                {stats.drafts}
              </div>
            </div>
          </div>

          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '22px 24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '12px',
                background: '#faf5ff',
                color: '#9333ea',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
              }}
            >
              🏷️
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                Categories
              </div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#9333ea', marginTop: '2px' }}>
                {stats.categoriesCount || categories.length || 3}
              </div>
            </div>
          </div>
        </div>

        {/* Filter and Control Bar */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '20px 24px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
            marginBottom: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '14px',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Search Input */}
            <div style={{ position: 'relative', flex: '1 1 300px', minWidth: '240px' }}>
              <span
                style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#94a3b8',
                  fontSize: '16px',
                }}
              >
                🔍
              </span>
              <input
                type="text"
                placeholder="Search questions, answers, categories..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '11px 16px 11px 40px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px',
                  outline: 'none',
                  background: '#f8fafc',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '11px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#ffffff',
                color: '#334155',
                cursor: 'pointer',
                minWidth: '160px',
              }}
            >
              <option value="all">All Categories</option>
              {categories.map((cat, i) => (
                <option key={i} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Audience Filter */}
            <select
              value={audienceFilter}
              onChange={(e) => {
                setAudienceFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '11px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#ffffff',
                color: '#334155',
                cursor: 'pointer',
                minWidth: '140px',
              }}
            >
              <option value="all">All Audiences</option>
              <option value="clients">Clients Only</option>
              <option value="therapists">Therapists Only</option>
              <option value="general">General Public</option>
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '11px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#ffffff',
                color: '#334155',
                cursor: 'pointer',
                minWidth: '130px',
              }}
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Drafts</option>
            </select>

            {/* Sort Filter */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '11px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#ffffff',
                color: '#334155',
                cursor: 'pointer',
                minWidth: '140px',
              }}
            >
              <option value="order">Display Order</option>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="question">Alphabetical (A-Z)</option>
              <option value="category">By Category</option>
            </select>

            {/* View Mode & Reset Buttons */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
                title={`Switch to ${viewMode === 'table' ? 'Card' : 'Table'} view`}
                style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#475569',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {viewMode === 'table' ? '🗂️ Card View' : '📑 Table View'}
              </button>

              {(searchQuery || categoryFilter !== 'all' || audienceFilter !== 'all' || statusFilter !== 'all') && (
                <button
                  onClick={handleClearFilters}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px dashed #ef4444',
                    background: '#fef2f2',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        {loading ? (
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '60px',
              textAlign: 'center',
              color: '#64748b',
              border: '1px solid #e2e8f0',
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Loading FAQ records...</p>
          </div>
        ) : faqs.length === 0 ? (
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '60px 40px',
              textAlign: 'center',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❓</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#1e293b' }}>No FAQs found</h3>
            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '15px' }}>
              {searchQuery || categoryFilter !== 'all'
                ? 'Try adjusting your search filters.'
                : 'Get started by creating your first frequently asked question.'}
            </p>
            <button
              onClick={handleOpenCreateModal}
              style={{
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              + Create FAQ
            </button>
          </div>
        ) : viewMode === 'table' ? (
          /* Table View */
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '16px 20px', fontSize: '13px', color: '#475569', fontWeight: 700, width: '60px' }}>
                      Order
                    </th>
                    <th style={{ padding: '16px 20px', fontSize: '13px', color: '#475569', fontWeight: 700, minWidth: '280px' }}>
                      Question & Answer
                    </th>
                    <th style={{ padding: '16px 20px', fontSize: '13px', color: '#475569', fontWeight: 700, width: '160px' }}>
                      Category
                    </th>
                    <th style={{ padding: '16px 20px', fontSize: '13px', color: '#475569', fontWeight: 700, width: '140px' }}>
                      Audience
                    </th>
                    <th style={{ padding: '16px 20px', fontSize: '13px', color: '#475569', fontWeight: 700, width: '110px' }}>
                      Status
                    </th>
                    <th style={{ padding: '16px 20px', fontSize: '13px', color: '#475569', fontWeight: 700, width: '150px', textAlign: 'right' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFaqs.map((faq, index) => {
                    const audienceInfo = AUDIENCE_LABELS[faq.targetAudience] || AUDIENCE_LABELS.all;
                    const isExpanded = expandedFaqId === faq._id;
                    const globalIndex = (currentPage - 1) * itemsPerPage + index;

                    return (
                      <tr
                        key={faq._id}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background 0.15s ease',
                          background: isExpanded ? '#faf5ff' : 'transparent',
                        }}
                      >
                        {/* Order Column & Reorder Buttons */}
                        <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: '13px',
                                color: '#6366f1',
                                background: '#e0e7ff',
                                borderRadius: '6px',
                                padding: '2px 8px',
                              }}
                            >
                              #{faq.order || globalIndex + 1}
                            </span>
                            <div style={{ display: 'flex', gap: '2px' }}>
                              <button
                                onClick={() => handleMoveOrder(globalIndex, 'up')}
                                disabled={globalIndex === 0}
                                title="Move up"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: globalIndex === 0 ? 'not-allowed' : 'pointer',
                                  color: globalIndex === 0 ? '#cbd5e1' : '#64748b',
                                  fontSize: '12px',
                                  padding: '2px',
                                }}
                              >
                                🔼
                              </button>
                              <button
                                onClick={() => handleMoveOrder(globalIndex, 'down')}
                                disabled={globalIndex === faqs.length - 1}
                                title="Move down"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: globalIndex === faqs.length - 1 ? 'not-allowed' : 'pointer',
                                  color: globalIndex === faqs.length - 1 ? '#cbd5e1' : '#64748b',
                                  fontSize: '12px',
                                  padding: '2px',
                                }}
                              >
                                🔽
                              </button>
                            </div>
                          </div>
                        </td>

                        {/* Question & Answer Column */}
                        <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                          <div
                            onClick={() => setExpandedFaqId(isExpanded ? null : faq._id)}
                            style={{
                              fontWeight: 700,
                              fontSize: '15px',
                              color: '#1e293b',
                              marginBottom: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span>{faq.question}</span>
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                              {isExpanded ? '▲ Collapse' : '▼ Preview'}
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: '14px',
                              color: '#64748b',
                              lineHeight: '1.5',
                              maxHeight: isExpanded ? 'none' : '42px',
                              overflow: 'hidden',
                              textOverflow: isExpanded ? 'clip' : 'ellipsis',
                              whiteSpace: isExpanded ? 'pre-line' : 'nowrap',
                            }}
                          >
                            {faq.answer}
                          </div>
                        </td>

                        {/* Category */}
                        <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                          <span
                            style={{
                              background: '#f1f5f9',
                              color: '#475569',
                              padding: '5px 12px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: 600,
                              display: 'inline-block',
                            }}
                          >
                            {faq.category || 'General'}
                          </span>
                        </td>

                        {/* Audience */}
                        <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                          <span
                            style={{
                              background: audienceInfo.bg,
                              color: audienceInfo.color,
                              padding: '5px 12px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span>{audienceInfo.icon}</span>
                            <span>{audienceInfo.label}</span>
                          </span>
                        </td>

                        {/* Status Toggle Switch */}
                        <td style={{ padding: '16px 20px', verticalAlign: 'top' }}>
                          <button
                            onClick={() => handleTogglePublish(faq)}
                            style={{
                              border: 'none',
                              background: faq.isPublished ? '#dcfce7' : '#fef3c7',
                              color: faq.isPublished ? '#15803d' : '#b45309',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.2s',
                            }}
                            title="Click to toggle publish status"
                          >
                            <span
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: faq.isPublished ? '#22c55e' : '#f59e0b',
                              }}
                            />
                            {faq.isPublished ? 'Published' : 'Draft'}
                          </button>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '16px 20px', verticalAlign: 'top', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleOpenEditModal(faq)}
                              style={{
                                background: '#eff6ff',
                                color: '#2563eb',
                                border: '1px solid #bfdbfe',
                                borderRadius: '8px',
                                padding: '7px 12px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                              }}
                              title="Edit FAQ"
                            >
                              ✏️ Edit
                            </button>

                            <button
                              onClick={() => setDeleteModal({ isOpen: true, item: faq, loading: false })}
                              style={{
                                background: '#fef2f2',
                                color: '#dc2626',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '7px 12px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                              }}
                              title="Delete FAQ"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination inside Table View */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={faqs.length}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onItemsPerPageChange={(limit) => {
                setItemsPerPage(limit);
                setCurrentPage(1);
              }}
            />
          </div>
        ) : (
          /* Card Grid View */
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '20px',
              }}
            >
              {paginatedFaqs.map((faq, index) => {
                const audienceInfo = AUDIENCE_LABELS[faq.targetAudience] || AUDIENCE_LABELS.all;
                const globalIndex = (currentPage - 1) * itemsPerPage + index;

                return (
                  <div
                    key={faq._id}
                    style={{
                      background: '#ffffff',
                      borderRadius: '16px',
                      padding: '24px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      position: 'relative',
                    }}
                  >
                    <div>
                      {/* Card Badges Row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: '12px',
                              color: '#6366f1',
                              background: '#e0e7ff',
                              borderRadius: '6px',
                              padding: '2px 8px',
                            }}
                          >
                            #{faq.order || globalIndex + 1}
                          </span>
                          <span
                            style={{
                              background: '#f1f5f9',
                              color: '#475569',
                              padding: '3px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            {faq.category || 'General'}
                          </span>
                        </div>

                        <button
                          onClick={() => handleTogglePublish(faq)}
                          style={{
                            border: 'none',
                            background: faq.isPublished ? '#dcfce7' : '#fef3c7',
                            color: faq.isPublished ? '#15803d' : '#b45309',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {faq.isPublished ? '● Live' : '○ Draft'}
                        </button>
                      </div>

                      {/* Question */}
                      <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 700, color: '#1e293b', lineHeight: '1.4' }}>
                        {faq.question}
                      </h3>

                      {/* Answer */}
                      <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#64748b', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                        {faq.answer}
                      </p>
                    </div>

                    {/* Card Bottom Meta & Actions */}
                    <div
                      style={{
                        borderTop: '1px solid #f1f5f9',
                        paddingTop: '14px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          background: audienceInfo.bg,
                          color: audienceInfo.color,
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <span>{audienceInfo.icon}</span>
                        <span>{audienceInfo.label}</span>
                      </span>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleOpenEditModal(faq)}
                          style={{
                            background: '#eff6ff',
                            color: '#2563eb',
                            border: '1px solid #bfdbfe',
                            borderRadius: '8px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => setDeleteModal({ isOpen: true, item: faq, loading: false })}
                          style={{
                            background: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            padding: '6px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination inside Card View */}
            <div style={{ marginTop: '24px' }}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={faqs.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
                onItemsPerPageChange={(limit) => {
                  setItemsPerPage(limit);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        )}

        {/* ================= CREATE / EDIT MODAL ================= */}
        {isModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: '20px',
                width: '100%',
                maxWidth: '640px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: '1px solid #e2e8f0',
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  padding: '24px 28px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                  borderTopLeftRadius: '20px',
                  borderTopRightRadius: '20px',
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
                    {modalMode === 'create' ? '✨ Add New FAQ' : '✏️ Edit FAQ'}
                  </h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    {modalMode === 'create'
                      ? 'Create a new question and answer pair for your website and users.'
                      : 'Update the question, details, or publishing settings.'}
                  </p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    background: '#e2e8f0',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#64748b',
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body Form */}
              <form onSubmit={handleSubmitForm} style={{ padding: '28px' }}>
                {modalError && (
                  <div
                    style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      marginBottom: '20px',
                      fontWeight: 500,
                    }}
                  >
                    ⚠️ {modalError}
                  </div>
                )}

                {/* Question Input */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: '#334155', marginBottom: '8px' }}>
                    Question <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. What should I expect in my first therapy session?"
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '15px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Answer Textarea */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: '#334155', marginBottom: '8px' }}>
                    Answer Content <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea
                    required
                    rows={5}
                    placeholder="Provide a clear, detailed, and helpful answer for your users..."
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      outline: 'none',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Category & Target Audience Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: '#334155', marginBottom: '8px' }}>
                      Category
                    </label>
                    <input
                      type="text"
                      list="faq-category-options"
                      placeholder="Select or type category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '11px 14px',
                        borderRadius: '10px',
                        border: '1px solid #cbd5e1',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                    <datalist id="faq-category-options">
                      {PRESET_CATEGORIES.map((cat, i) => (
                        <option key={i} value={cat} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: '#334155', marginBottom: '8px' }}>
                      Target Audience
                    </label>
                    <select
                      value={formData.targetAudience}
                      onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '11px 14px',
                        borderRadius: '10px',
                        border: '1px solid #cbd5e1',
                        fontSize: '14px',
                        background: '#ffffff',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="all">🌐 All Users (Public)</option>
                      <option value="clients">👤 Clients / Patients</option>
                      <option value="therapists">👨‍⚕️ Therapists / Practitioners</option>
                      <option value="general">📢 General Public</option>
                    </select>
                  </div>
                </div>

                {/* Order & Publishing Status Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: '#334155', marginBottom: '8px' }}>
                      Display Order Priority
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) || 1 })}
                      style={{
                        width: '100%',
                        padding: '11px 14px',
                        borderRadius: '10px',
                        border: '1px solid #cbd5e1',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                    <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                      Lower numbers appear first.
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <label style={{ fontWeight: 700, fontSize: '14px', color: '#334155', marginBottom: '8px' }}>
                      Publish Status
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        cursor: 'pointer',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        background: formData.isPublished ? '#f0fdf4' : '#f8fafc',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.isPublished}
                        onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#16a34a' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 600, color: formData.isPublished ? '#166534' : '#64748b' }}>
                        {formData.isPublished ? '✅ Published (Visible)' : '🔒 Draft (Hidden)'}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Modal Footer Buttons */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    paddingTop: '20px',
                    borderTop: '1px solid #e2e8f0',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    style={{
                      padding: '12px 20px',
                      borderRadius: '10px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '10px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '14px',
                      cursor: formSubmitting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
                    }}
                  >
                    {formSubmitting ? 'Saving...' : modalMode === 'create' ? 'Create FAQ' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ================= DELETE CONFIRMATION MODAL ================= */}
        {deleteModal.isOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '460px',
                padding: '28px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: '#fef2f2',
                  color: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  margin: '0 auto 16px auto',
                }}
              >
                🗑️
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                Delete FAQ Question?
              </h3>
              <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                Are you sure you want to delete &ldquo;<b>{deleteModal.item?.question}</b>&rdquo;? This action cannot be undone.
              </p>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setDeleteModal({ isOpen: false, item: null, loading: false })}
                  disabled={deleteModal.loading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteModal.loading}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#ef4444',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: deleteModal.loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                  }}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminFaqsPage;
